/**
 * ResendPlanList — one line per killed drop: what it was, when the new drop
 * goes out (Danish + recipient local time), and — after the run — what
 * happened to it. Drops already re-sent are listed last, as skipped.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { AlertTriangle, CheckCircle2, MinusCircle, XCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { describeCountryLocalSend } from '@/utils/country-timezone';
import { formatDateDMY, formatDateTimeDMYHM } from '@/features/smswiser-campaigns/utils/dateFormat';

import type { ResendPlan, ResendPlanRow } from '@/features/resend-killed-drops/planResend';
import type { ResendOutcome } from '@/features/resend-killed-drops/resendKilledApi';

const When: FC<{ row: ResendPlanRow }> = ({ row }) => {
  if (!row.sendAt) return <span className="font-medium text-foreground">Now</span>;
  const iso = row.sendAt.toISOString();
  const local = describeCountryLocalSend(iso, row.drop.countryKey);
  const movesDay = row.workDate !== row.drop.workDate.slice(0, 10);
  return (
    <span className="flex flex-wrap items-baseline justify-end gap-x-1.5">
      <span className="font-medium tabular-nums text-foreground">{formatDateTimeDMYHM(iso)}</span>
      {local && (
        <span className="tabular-nums text-muted-foreground" title={`${local.full} · ${local.tzLabel}`}>
          {local.time}
          {local.dayDelta !== 0 && (local.dayDelta > 0 ? ' (+1)' : ' (−1)')} local
        </span>
      )}
      {movesDay && (
        <span
          className="rounded bg-muted px-1 text-[10px] text-muted-foreground"
          title="It goes out on another day, so it joins that day's list"
        >
          {formatDateDMY(row.workDate)} list
        </span>
      )}
    </span>
  );
};

const Outcome: FC<{ outcome: ResendOutcome }> = ({ outcome }) => {
  if (outcome.status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1 font-medium text-success">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        Re-sent as #{outcome.dropId}
      </span>
    );
  }
  const Icon = outcome.status === 'skipped' ? MinusCircle : outcome.ambiguous ? AlertTriangle : XCircle;
  const tone = outcome.status === 'skipped' ? 'text-muted-foreground' : outcome.ambiguous ? 'text-warning' : 'text-destructive';
  return (
    <span className={cn('inline-flex items-start gap-1 text-left', tone)}>
      <Icon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="min-w-0 break-words">{outcome.message}</span>
    </span>
  );
};

const Row: FC<{ row: ResendPlanRow; outcome?: ResendOutcome }> = ({ row, outcome }) => {
  const { drop } = row;
  return (
    <li className={cn('grid grid-cols-[minmax(0,1fr)_minmax(0,auto)] items-center gap-3 px-3 py-2', row.blocked && 'opacity-60')}>
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-foreground" title={drop.sentTitle ?? drop.titleBase}>
          {drop.sentTitle ?? drop.titleBase}
        </div>
        <div className="truncate text-[11px] tabular-nums text-muted-foreground">
          {drop.sentDropId != null ? `#${drop.sentDropId} · ` : ''}
          {drop.amount.toLocaleString('en-US')} SMS
        </div>
      </div>
      <div className="max-w-[18rem] text-right text-xs">
        {outcome ? <Outcome outcome={outcome} /> : row.blocked ? <span className="text-muted-foreground">Skipped — {row.blocked}</span> : <When row={row} />}
      </div>
    </li>
  );
};

export const ResendPlanList: FC<{ plan: ResendPlan; outcomes?: Map<string, ResendOutcome> }> = ({
  plan,
  outcomes,
}) => (
  <section className="space-y-2" aria-label="Drops">
    <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
      {plan.ready.length} drop{plan.ready.length === 1 ? '' : 's'} to re-send
      {plan.blocked.length > 0 && ` · ${plan.blocked.length} skipped`}
    </div>
    <ul className="max-h-72 divide-y divide-border/60 overflow-y-auto rounded-md border border-border/60 bg-muted/10">
      {[...plan.ready, ...plan.blocked].map((row) => (
        <Row key={row.drop.id} row={row} outcome={outcomes?.get(row.drop.id)} />
      ))}
    </ul>
  </section>
);
